import React from 'react'
import axios from 'axios'
import FlatPagination from 'material-ui-flat-pagination'
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Title from '../atoms/Title'
import moment from 'moment'
import MessageAllHistoryDetailModal from './MessageAllHistoryDetailModal'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3)
  },
  badge: {
    '& .MuiBadge-badge': {
      height: 'auto',
      padding: 3,
      borderRadius: '50%',
      backgroundColor: '#FF6363',
      color: '#fff'
    }
  },
  searchLabel: {
    maxWidth: '142px'
  },
  searchItem: {
    '@media (min-width: 1056px)': {
      flexGrow: 1,
      maxWidth: 'none',
      flexBasis: 'none'
    }
  }
}))

const MessageAllHistoryList: React.FC = () => {
  const LIMIT = 10

  const classes = useStyles({})
  const [messageSubjects, setMessageSubjects] = React.useState<any[]>([])
  const [total, setTotal] = React.useState<number>(0)
  const [page, setPage] = React.useState<number>(1)
  const [detailModalState, setDetailModalState] = React.useState<{
    isOpen: boolean
    subjectGroupId: number | null
  }>({
    isOpen: false,
    subjectGroupId: null
  })

  const fetchHistoryMessages = React.useCallback(async () => {
    const { data } = await axios.get(`/messages/subject-groups/histories`, {})
    setMessageSubjects(data.subjects)
    setTotal(data.subjects.length)
  }, [])

  React.useEffect(() => {
    fetchHistoryMessages()
  }, [fetchHistoryMessages])

  const handlePageChange = async (page: number) => {
    try {
      setPage(page)
    } catch (error) {
      alert(error.message)
    }
  }

  const getPageInfo = React.useCallback(() => {
    const totalPage = Math.ceil(total / LIMIT)
    const startNumber = total === 0 ? 0 : (page - 1) * LIMIT + 1
    const lastNumber = page === totalPage ? total : page * LIMIT
    return { total, totalPage, startNumber, lastNumber }
  }, [page, total])

  const CurrentPageCaptionComponent = React.useCallback(() => {
    const { total, startNumber, lastNumber } = getPageInfo()
    return (
      <Typography variant="caption" align="center" display="block" color="primary">
        全 {total} 件｜ ({startNumber} - {lastNumber}件)
      </Typography>
    )
  }, [getPageInfo])

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper} square elevation={2}>
          <Title>メッセージ履歴</Title>
          <Box>
            <Box display="flex" justifyContent="center">
              {CurrentPageCaptionComponent}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={LIMIT}
                offset={(page - 1) * LIMIT}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={15}>送信日時</TableCell>
                <TableCell>メッセージ件名</TableCell>
                <TableCell width={15}>既読件数 / 送信件数</TableCell>
                <TableCell width={10} align="center">
                  詳細
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messageSubjects
                .filter((_, index) => {
                  const { startNumber, lastNumber } = getPageInfo()
                  return index >= startNumber - 1 && index <= lastNumber - 1
                })
                .map(subject => {
                  return (
                    <TableRow key={subject.id}>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        {moment(subject.created_at).format('YYYY/MM/DD HH:mm')}
                      </TableCell>
                      <TableCell>{subject.subject}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        {subject.total_cnt - subject.read_cnt > 0 ? (
                          <Typography variant="inherit" color="secondary">
                            {subject.read_cnt} / {subject.total_cnt} （会員未読あり）
                          </Typography>
                        ) : (
                          <Typography variant="inherit">
                            {subject.read_cnt} / {subject.total_cnt}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setDetailModalState({
                              isOpen: true,
                              subjectGroupId: subject.subject_group_id
                            })
                          }}
                        >
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
          <Box my={4}>
            <Box display="flex" justifyContent="center">
              {CurrentPageCaptionComponent}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={LIMIT}
                offset={(page - 1) * LIMIT}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>
      <MessageAllHistoryDetailModal
        isOpen={detailModalState.isOpen}
        closeHandler={() => {
          setDetailModalState({ isOpen: false, subjectGroupId: null })
        }}
        subjectGroupId={detailModalState.subjectGroupId}
      />
    </React.Fragment>
  )
}

export default MessageAllHistoryList
